import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { saveDistrictKeys } from '../engine/districtSelection'
import { supabase } from '../lib/supabase'
import { switchPreferenceScope } from '../lib/preferenceScope'

import { BETA_AGREEMENT_VERSION } from '../engine/guestSession'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [userRole, setUserRole] = useState(null)
  const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false)
  const [userPrefs, setUserPrefs] = useState(null)

  const profileEpoch = useRef(0)
  const preferenceEpoch = useRef(0)
  const preferenceWrites = useRef(Promise.resolve())
  const currentUserId = useRef(null)

  const clearProfile = useCallback(() => {
    profileEpoch.current++
    setUserRole(null)
    setHasAcceptedAgreement(false)
    setUserPrefs(null)
    setProfileLoading(false)
  }, [])

  const loadProfile = useCallback(async (userId, authUser) => {
    if (!userId) {
      clearProfile()
      return
    }

    const epoch = ++profileEpoch.current
    const prefsEpoch = preferenceEpoch.current
    setUserRole(null)
    setHasAcceptedAgreement(false)
    setProfileLoading(true)
    try {
      const results = await Promise.allSettled([
        supabase
          .from('beta_agreement_acceptances')
          .select('id')
          .eq('user_id', userId)
          .eq('agreement_version', BETA_AGREEMENT_VERSION)
          .maybeSingle(),
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('user_preferences')
          .select('state, district, weights, stances')
          .eq('user_id', userId)
          .maybeSingle(),
      ])

      if (epoch !== profileEpoch.current) return
      const [agreementResult, roleResult, prefsResult] = results.map(result =>
        result.status === 'fulfilled' ? result.value : { error: result.reason })
      setUserRole(roleResult.error ? null : (roleResult.data?.role ?? 'user'))
      if (!prefsResult.error && prefsEpoch === preferenceEpoch.current) {
        setUserPrefs(prefsResult.data ?? null)
        if (prefsResult.data?.state !== localStorage.getItem('civitas_state') ||
            (prefsResult.data?.district || '') !== (localStorage.getItem('civitas_district') || '')) {
          saveDistrictKeys([])
        }
        if (prefsResult.data?.state) localStorage.setItem('civitas_state', prefsResult.data.state)
        else localStorage.removeItem('civitas_state')
        localStorage.setItem('civitas_district', prefsResult.data?.district || '')
      }
      if (agreementResult.error) {
        setHasAcceptedAgreement(false)
        return
      }
      if (agreementResult.data) {
        if (epoch === profileEpoch.current) setHasAcceptedAgreement(true)
      } else if (authUser?.user_metadata?.beta_agreement_accepted &&
          authUser.user_metadata.beta_agreement_version === BETA_AGREEMENT_VERSION &&
          Number.isFinite(Date.parse(authUser.user_metadata.beta_agreement_accepted_at))) {
        // Public feedback and agreement reading must never persist consent,
        // including signup metadata recovery. Explicit acceptance has its own flow.
        if (/^\/feedback\/?$/.test(window.location.pathname) ||
            (/^\/beta-agreement\/?$/.test(window.location.pathname) &&
            new URLSearchParams(window.location.search).get('view') === '1')) {
          setHasAcceptedAgreement(false)
          return
        }
        const { error } = await supabase.from('beta_agreement_acceptances').upsert({
          user_id: userId,
          agreement_version: BETA_AGREEMENT_VERSION,
          accepted_at: authUser.user_metadata.beta_agreement_accepted_at,
        }, {
          onConflict: 'user_id,agreement_version',
          ignoreDuplicates: true,
        })
        if (error) {
          setHasAcceptedAgreement(false)
          return
        }
        if (epoch === profileEpoch.current) setHasAcceptedAgreement(true)
      } else {
        setHasAcceptedAgreement(false)
      }
    } catch (error) {
      if (epoch !== profileEpoch.current) return
      console.error('Failed to load authenticated profile', error)
      setUserRole(null)
      setHasAcceptedAgreement(false)
      setUserPrefs(null)
    } finally {
      if (epoch === profileEpoch.current) setProfileLoading(false)
    }
  }, [clearProfile])

  useEffect(() => {
    let active = true

    const applySession = async nextSession => {
      if (!active) return
      if (currentUserId.current !== (nextSession?.user?.id ?? null)) {
        clearProfile()
        currentUserId.current = nextSession?.user?.id ?? null
      }
      switchPreferenceScope(nextSession?.user?.id)
      setSession(nextSession)
      setUser(nextSession?.user ?? null)
      await loadProfile(nextSession?.user?.id, nextSession?.user)
      if (active) setLoading(false)
    }

    supabase.auth.getSession().then(({ data }) => applySession(data.session))

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      applySession(nextSession)
    })

    return () => {
      active = false
      profileEpoch.current++
      subscription.unsubscribe()
    }
  }, [loadProfile, clearProfile])

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }, [])

  const signUp = useCallback(async (email, password, { betaAgreementAccepted = false } = {}) => {
    const acceptedAt = betaAgreementAccepted ? new Date().toISOString() : null
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
          beta_agreement_accepted: betaAgreementAccepted,
          beta_agreement_version: betaAgreementAccepted ? BETA_AGREEMENT_VERSION : null,
          beta_agreement_accepted_at: acceptedAt,
        },
      },
    })
    if (error) throw error
    return data
  }, [])

  const signInWithGoogle = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
    if (error) throw error
    return data
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    clearProfile()
  }, [clearProfile])

  const resetPassword = useCallback(async email => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
    return data
  }, [])

  const acceptBetaAgreement = useCallback(async () => {
    if (!user) throw new Error('Must be signed in to accept the agreement.')

    const acceptingUserId = user.id
    const { error } = await supabase.from('beta_agreement_acceptances').upsert({
      user_id: user.id,
      agreement_version: BETA_AGREEMENT_VERSION,
      accepted_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,agreement_version',
      ignoreDuplicates: true,
    })
    if (error) throw error
    if (currentUserId.current === acceptingUserId) setHasAcceptedAgreement(true)
  }, [user])

  const saveUserPrefs = useCallback(async (state, district, weights, stances) => {
    const saveEpoch = ++preferenceEpoch.current
    const patch = Object.fromEntries(Object.entries({ state, district, weights, stances })
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, value === '' ? null : value]))
    const savingUserId = user?.id
    if (savingUserId) {
      const write = preferenceWrites.current.catch(() => {}).then(() => supabase.from('user_preferences').upsert({
        user_id: savingUserId, ...patch, updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' }))
      preferenceWrites.current = write
      const { error } = await write
      if (error) throw error
      if (currentUserId.current !== savingUserId || saveEpoch !== preferenceEpoch.current) return
      setUserPrefs(previous => ({ ...previous, ...patch }))
    }
    for (const key of ['state', 'district']) {
      if (!(key in patch)) continue
      if (patch[key] == null) localStorage.removeItem(`civitas_${key}`)
      else localStorage.setItem(`civitas_${key}`, patch[key])
    }
  }, [user])

  const value = {
    user,
    session,
    loading,
    profileLoading,
    userRole,
    role: userRole,
    isAdmin: userRole === 'admin',
    hasAcceptedAgreement,
    betaAgreementAccepted: hasAcceptedAgreement,
    userPrefs,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    acceptAgreement: acceptBetaAgreement,
    acceptBetaAgreement,
    saveUserPrefs,
    refreshProfileFlags: () => loadProfile(user?.id, user),
    supabase,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within an AuthProvider')
  return value
}

export default AuthContext
